/**
 * Craftpix Poker Kit chrome — backgrounds, panels, score HUD, sprite buttons.
 * Used by /lobby (and reusable on table/account shells).
 */
import type { CSSProperties, ReactNode, ButtonHTMLAttributes } from "react";
import {
  CRAFTPIX,
  themeBackground,
  type CraftpixTheme,
} from "@/lib/craftpix-poker-ui";
import { BRAND } from "@shared/brand";

export function CraftpixStage({
  theme = "green",
  children,
  className = "",
}: {
  theme?: CraftpixTheme;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`cp-stage ${className}`} data-theme={theme}>
      <div
        className="cp-stage__ambient"
        style={{
          backgroundImage: `url(${CRAFTPIX.generated.ambientLobby})`,
          opacity: 0.72,
          filter: "saturate(0.9) brightness(0.45)",
        }}
        aria-hidden
      />
      <div
        className="cp-stage__craftpix"
        style={{
          backgroundImage: `url(${themeBackground(theme)})`,
          opacity: 0.28,
        }}
        aria-hidden
      />
      <div
        className="cp-stage__vignette"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.72)), url(${CRAFTPIX.game.darkOverlay})`,
          opacity: 0.65,
        }}
        aria-hidden
      />
      <div className="cp-stage__content">{children}</div>
    </div>
  );
}

export function CraftpixPanelBar({
  children,
  className = "",
  style,
}: {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={`cp-panel-bar ${className}`}
      style={{
        backgroundImage: `url(${CRAFTPIX.lobby.panelBar})`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function CraftpixContainer({
  children,
  className = "",
  luxury = true,
  style,
}: {
  children: ReactNode;
  className?: string;
  luxury?: boolean;
  style?: CSSProperties;
}) {
  const wood = BRAND.panels.wood;
  return (
    <div
      className={`cp-container ${luxury ? "cp-container--luxury" : ""} ${className}`}
      style={{
        backgroundImage: luxury
          ? `linear-gradient(180deg, rgba(12,10,8,0.78), rgba(6,5,4,0.90)), url(${wood})`
          : `linear-gradient(180deg, rgba(12,10,8,0.82), rgba(6,5,4,0.92)), url(${wood})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function CraftpixWindowFrame({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`cp-window ${className}`}>
      <div
        className="cp-window__frame"
        style={{ backgroundImage: `url(${CRAFTPIX.lobby.window})` }}
        aria-hidden
      />
      <div className="cp-window__body">{children}</div>
    </div>
  );
}

export function CraftpixMoneyPill({
  amount,
  label = "GBUX",
  className = "",
}: {
  amount: string | number;
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={`cp-score-pill cp-score-pill--money ${className}`}
      style={{ backgroundImage: `url(${CRAFTPIX.lobby.moneyPanel})` }}
      data-testid="text-balance"
    >
      <span className="cp-score-pill__value">
        {typeof amount === "number" ? amount.toLocaleString() : amount}
      </span>
      <span className="cp-score-pill__label">{label}</span>
    </div>
  );
}

export function CraftpixChipPill({
  amount,
  label = "CHIPS",
  className = "",
}: {
  amount: string | number;
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={`cp-score-pill cp-score-pill--chip ${className}`}
      style={{ backgroundImage: `url(${CRAFTPIX.lobby.chipPanel})` }}
    >
      <span className="cp-score-pill__value">
        {typeof amount === "number" ? amount.toLocaleString() : amount}
      </span>
      <span className="cp-score-pill__label">{label}</span>
    </div>
  );
}

/**
 * Production chrome button — **readable system font only**.
 * Never use craftpix sprites with baked English (fold/play/shop) — they clash with our labels.
 * `src` is accepted for call-site compatibility but ignored (word-art purge).
 */
export function CraftpixSpriteButton({
  src: _src,
  alt,
  label,
  className = "",
  variant = "gold",
  ...rest
}: {
  /** @deprecated Ignored — pre-written button art purged for readability */
  src?: string;
  alt: string;
  label?: string;
  /** gold | green | red | slate | violet */
  variant?: "gold" | "green" | "red" | "slate" | "violet";
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const text = (label || alt || "OK").trim();
  return (
    <button
      type="button"
      className={`cp-text-btn cp-text-btn--${variant} ${className}`}
      aria-label={alt || text}
      {...rest}
    >
      <span className="cp-text-btn__label">{text}</span>
    </button>
  );
}

export function CraftpixPlayerCard({
  name,
  children,
  className = "",
}: {
  name?: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`cp-player-card ${className}`}
      style={{ backgroundImage: `url(${CRAFTPIX.menu.playerPanel})` }}
    >
      <img
        src={CRAFTPIX.lobby.avatar}
        alt=""
        className="cp-player-card__avatar"
        draggable={false}
      />
      <div className="cp-player-card__meta">
        {name ? <div className="cp-player-card__name">{name}</div> : null}
        {children}
      </div>
    </div>
  );
}

export function CraftpixSectionTitle({
  children,
  accent = "#d4af37",
}: {
  children: ReactNode;
  accent?: string;
}) {
  return (
    <div className="cp-section-title" style={{ ["--cp-accent" as string]: accent }}>
      <span className="cp-section-title__rule" />
      <h2 className="cp-section-title__text">{children}</h2>
      <span className="cp-section-title__rule" />
    </div>
  );
}
